import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Header from '../../components/layout/Header';
import Footer from '../../components/layout/Footer';
import ProductCard from '../../components/ui/ProductCard';
import { allProducts } from '../../mocks/products';
import { useCart } from '../../contexts/CartContext';

const ProductPage = () => {
  const { id } = useParams();
  const { addToCart } = useCart();
  const [product, setProduct] = useState<any>(null);
  const [selectedImage, setSelectedImage] = useState(0);
  const [quantity, setQuantity] = useState(1);
  const [showAddedToast, setShowAddedToast] = useState(false);
  const [activeTab, setActiveTab] = useState<'description' | 'specs' | 'reviews'>('description');

  useEffect(() => {
    const foundProduct = allProducts.find(p => p.id === id);
    if (foundProduct) {
      setProduct(foundProduct);
    }
  }, [id]);

  if (!product) {
    return (
      <div className="min-h-screen bg-[#F5F5F5] flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-[#0D47A1] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">جاري التحميل...</p>
        </div>
      </div>
    );
  }

  const relatedProducts = allProducts
    .filter(p => p.categoryId === product.categoryId && p.id !== product.id)
    .slice(0, 4);

  const handleAddToCart = () => {
    addToCart({ ...product, quantity });
    setShowAddedToast(true);
    setTimeout(() => setShowAddedToast(false), 3000);
  };

  const images = [product.image, product.image, product.image, product.image];

  return (
    <div className="min-h-screen bg-[#F5F5F5]">
      <Header />

      {/* Breadcrumb */}
      <div className="bg-white border-b">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center gap-2 text-sm text-gray-600">
            <Link to="/" className="hover:text-[#0D47A1] cursor-pointer">الرئيسية</Link>
            <i className="ri-arrow-left-s-line"></i>
            <Link to="/categories" className="hover:text-[#0D47A1] cursor-pointer">الأقسام</Link>
            <i className="ri-arrow-left-s-line"></i>
            <span className="text-gray-800 font-medium">{product.name}</span>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-12">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 mb-16">
          {/* Product Images */}
          <div>
            <div className="bg-white rounded-2xl shadow-lg overflow-hidden mb-4">
              <div className="relative aspect-square">
                <img
                  src={images[selectedImage]}
                  alt={product.name}
                  className="w-full h-full object-cover transition-transform duration-500 hover:scale-110"
                />
                {product.discount && (
                  <div className="absolute top-4 right-4 bg-red-500 text-white px-4 py-2 rounded-full font-bold">
                    خصم {product.discount}%
                  </div>
                )}
                {product.badge && (
                  <div className="absolute top-4 left-4 bg-[#FF8F00] text-white px-4 py-2 rounded-full font-bold">
                    {product.badge}
                  </div>
                )}
              </div>
            </div>
            <div className="grid grid-cols-4 gap-4">
              {images.map((img, index) => (
                <button
                  key={index}
                  onClick={() => setSelectedImage(index)}
                  className={`bg-white rounded-xl overflow-hidden border-2 transition-all cursor-pointer ${
                    selectedImage === index ? 'border-[#0D47A1]' : 'border-transparent hover:border-gray-300'
                  }`}
                >
                  <img src={img} alt={`صورة ${index + 1}`} className="w-full aspect-square object-cover" />
                </button>
              ))}
            </div>
          </div>

          {/* Product Info */}
          <div>
            <div className="bg-white rounded-2xl shadow-lg p-8">
              <div className="flex items-start justify-between mb-4">
                <div>
                  <h1 className="text-3xl font-bold text-gray-800 mb-2">{product.name}</h1>
                  <p className="text-gray-600">{product.brand}</p>
                </div>
                <button className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center hover:bg-red-50 hover:text-red-500 transition-colors cursor-pointer">
                  <i className="ri-heart-line text-2xl"></i>
                </button>
              </div>

              {/* Rating */}
              <div className="flex items-center gap-4 mb-6 pb-6 border-b">
                <div className="flex items-center gap-1">
                  {[...Array(5)].map((_, i) => (
                    <i
                      key={i}
                      className={`${
                        i < Math.floor(product.rating)
                          ? 'ri-star-fill text-yellow-400'
                          : 'ri-star-line text-gray-300'
                      }`}
                    ></i>
                  ))}
                </div>
                <span className="text-gray-600">({product.reviews} تقييم)</span>
              </div>

              {/* Price */}
              <div className="mb-8">
                <div className="flex items-baseline gap-3 mb-2">
                  <span className="text-4xl font-bold text-[#0D47A1]">
                    {product.price.toLocaleString('ar-SA')} ريال
                  </span>
                  {product.oldPrice && (
                    <span className="text-xl text-gray-400 line-through">
                      {product.oldPrice.toLocaleString('ar-SA')} ريال
                    </span>
                  )}
                </div>
                <p className="text-sm text-gray-600">شامل ضريبة القيمة المضافة</p>
              </div>

              {/* Stock Status */}
              <div className="mb-6">
                {product.inStock ? (
                  <div className="flex items-center gap-2 text-green-600">
                    <i className="ri-checkbox-circle-fill"></i>
                    <span className="font-medium">متوفر في المخزون</span>
                  </div>
                ) : (
                  <div className="flex items-center gap-2 text-red-600">
                    <i className="ri-close-circle-fill"></i>
                    <span className="font-medium">غير متوفر حالياً</span>
                  </div>
                )}
              </div>

              {/* Quantity Selector */}
              <div className="mb-8">
                <label className="block text-gray-700 font-medium mb-3">الكمية</label>
                <div className="flex items-center gap-4">
                  <div className="flex items-center border-2 border-gray-200 rounded-lg">
                    <button
                      onClick={() => setQuantity(Math.max(1, quantity - 1))}
                      className="w-12 h-12 flex items-center justify-center hover:bg-gray-50 transition-colors cursor-pointer"
                    >
                      <i className="ri-subtract-line text-xl"></i>
                    </button>
                    <input
                      type="number"
                      value={quantity}
                      onChange={(e) => setQuantity(Math.max(1, parseInt(e.target.value) || 1))}
                      className="w-16 h-12 text-center border-x-2 border-gray-200 focus:outline-none"
                    />
                    <button
                      onClick={() => setQuantity(quantity + 1)}
                      className="w-12 h-12 flex items-center justify-center hover:bg-gray-50 transition-colors cursor-pointer"
                    >
                      <i className="ri-add-line text-xl"></i>
                    </button>
                  </div>
                  <span className="text-gray-600">
                    الإجمالي: <span className="font-bold text-[#0D47A1]">{(product.price * quantity).toLocaleString('ar-SA')} ريال</span>
                  </span>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex gap-4 mb-6">
                <button
                  onClick={handleAddToCart}
                  disabled={!product.inStock}
                  className="flex-1 bg-[#0D47A1] text-white py-4 rounded-lg font-bold text-lg hover:bg-[#0a3a8a] transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed cursor-pointer whitespace-nowrap flex items-center justify-center gap-2"
                >
                  <i className="ri-shopping-cart-line text-xl"></i>
                  إضافة للسلة
                </button>
                <button className="w-14 h-14 bg-[#FF8F00] text-white rounded-lg hover:bg-[#F57C00] transition-colors cursor-pointer flex items-center justify-center">
                  <i className="ri-share-line text-xl"></i>
                </button>
              </div>

              {/* Features */}
              <div className="grid grid-cols-2 gap-4 pt-6 border-t">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-blue-50 rounded-lg flex items-center justify-center">
                    <i className="ri-truck-line text-[#0D47A1]"></i>
                  </div>
                  <div>
                    <p className="font-medium text-gray-800">شحن مجاني</p>
                    <p className="text-xs text-gray-600">للطلبات فوق 500 ريال</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-green-50 rounded-lg flex items-center justify-center">
                    <i className="ri-shield-check-line text-green-600"></i>
                  </div>
                  <div>
                    <p className="font-medium text-gray-800">ضمان سنتين</p>
                    <p className="text-xs text-gray-600">ضمان الوكيل</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-orange-50 rounded-lg flex items-center justify-center">
                    <i className="ri-refresh-line text-[#FF8F00]"></i>
                  </div>
                  <div>
                    <p className="font-medium text-gray-800">إرجاع مجاني</p>
                    <p className="text-xs text-gray-600">خلال 14 يوم</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-purple-50 rounded-lg flex items-center justify-center">
                    <i className="ri-customer-service-2-line text-purple-600"></i>
                  </div>
                  <div>
                    <p className="font-medium text-gray-800">دعم فني</p>
                    <p className="text-xs text-gray-600">على مدار الساعة</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Product Details Tabs */}
        <div className="bg-white rounded-2xl shadow-lg p-8 mb-16">
          <div className="flex gap-4 mb-8 border-b">
            <button
              onClick={() => setActiveTab('description')}
              className={`px-6 py-3 font-medium transition-all cursor-pointer whitespace-nowrap ${
                activeTab === 'description'
                  ? 'text-[#0D47A1] border-b-2 border-[#0D47A1]'
                  : 'text-gray-600 hover:text-gray-800'
              }`}
            >
              الوصف
            </button>
            <button
              onClick={() => setActiveTab('specs')}
              className={`px-6 py-3 font-medium transition-all cursor-pointer whitespace-nowrap ${
                activeTab === 'specs'
                  ? 'text-[#0D47A1] border-b-2 border-[#0D47A1]'
                  : 'text-gray-600 hover:text-gray-800'
              }`}
            >
              المواصفات الفنية
            </button>
            <button
              onClick={() => setActiveTab('reviews')}
              className={`px-6 py-3 font-medium transition-all cursor-pointer whitespace-nowrap ${
                activeTab === 'reviews'
                  ? 'text-[#0D47A1] border-b-2 border-[#0D47A1]'
                  : 'text-gray-600 hover:text-gray-800'
              }`}
            >
              التقييمات ({product.reviews})
            </button>
          </div>

          {activeTab === 'description' && (
            <div className="prose max-w-none">
              <p className="text-gray-700 leading-relaxed text-lg mb-4">
                {product.name} من {product.brand} - منتج عالي الجودة مصمم لتلبية احتياجاتك اليومية بكفاءة وموثوقية عالية.
              </p>
              <p className="text-gray-700 leading-relaxed mb-4">
                يتميز هذا المنتج بتقنيات حديثة وتصميم عصري يجمع بين الأداء الممتاز والمتانة. مثالي للاستخدام المنزلي والتجاري على حد سواء.
              </p>
              <h3 className="text-xl font-bold text-gray-800 mb-3">المميزات الرئيسية:</h3>
              <ul className="list-disc list-inside space-y-2 text-gray-700 mb-4">
                <li>تقنية حديثة وموفرة للطاقة</li>
                <li>تصميم عصري وأنيق يناسب جميع الديكورات</li>
                <li>سهولة في الاستخدام والصيانة</li>
                <li>ضمان الوكيل لمدة سنتين</li>
                <li>خدمة ما بعد البيع المتميزة</li>
              </ul>
            </div>
          )}

          {activeTab === 'specs' && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="flex items-center justify-between py-3 border-b">
                <span className="text-gray-600">الماركة</span>
                <span className="font-medium text-gray-800">{product.brand}</span>
              </div>
              <div className="flex items-center justify-between py-3 border-b">
                <span className="text-gray-600">رقم الموديل</span>
                <span className="font-medium text-gray-800">EM-{product.id}</span>
              </div>
              <div className="flex items-center justify-between py-3 border-b">
                <span className="text-gray-600">بلد المنشأ</span>
                <span className="font-medium text-gray-800">كوريا الجنوبية</span>
              </div>
              <div className="flex items-center justify-between py-3 border-b">
                <span className="text-gray-600">الضمان</span>
                <span className="font-medium text-gray-800">سنتان</span>
              </div>
              <div className="flex items-center justify-between py-3 border-b">
                <span className="text-gray-600">استهلاك الطاقة</span>
                <span className="font-medium text-gray-800">A++</span>
              </div>
              <div className="flex items-center justify-between py-3 border-b">
                <span className="text-gray-600">الوزن</span>
                <span className="font-medium text-gray-800">45 كجم</span>
              </div>
            </div>
          )}

          {activeTab === 'reviews' && (
            <div className="space-y-6">
              {[1, 2, 3].map((review) => (
                <div key={review} className="border-b pb-6 last:border-b-0">
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <div className="flex items-center gap-3 mb-2">
                        <div className="w-10 h-10 bg-[#0D47A1] rounded-full flex items-center justify-center text-white font-bold">
                          م
                        </div>
                        <div>
                          <p className="font-bold text-gray-800">محمد العتيبي</p>
                          <p className="text-sm text-gray-600">منذ أسبوعين</p>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-1">
                      {[...Array(5)].map((_, i) => (
                        <i key={i} className="ri-star-fill text-yellow-400"></i>
                      ))}
                    </div>
                  </div>
                  <p className="text-gray-700 leading-relaxed">
                    منتج ممتاز وجودة عالية، التوصيل كان سريع والتعامل احترافي. أنصح بالشراء من متجر العمران.
                  </p>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Related Products */}
        {relatedProducts.length > 0 && (
          <section>
            <h2 className="text-3xl font-bold text-gray-800 mb-8">منتجات ذات صلة</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {relatedProducts.map((relatedProduct) => (
                <ProductCard key={relatedProduct.id} {...relatedProduct} />
              ))}
            </div>
          </section>
        )}
      </div>

      {/* Added to Cart Toast */}
      {showAddedToast && (
        <div className="fixed bottom-8 left-1/2 -translate-x-1/2 bg-green-600 text-white px-8 py-4 rounded-lg shadow-2xl z-50 animate-fade-in flex items-center gap-3">
          <i className="ri-checkbox-circle-fill text-2xl"></i>
          <span className="font-medium">تمت الإضافة للسلة بنجاح!</span>
        </div>
      )}

      <Footer />
    </div>
  );
};

export default ProductPage;